import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { ApprovalItem } from "@/types/approval";
import { ApprovalCard } from "@/components/ApprovalCard";
import { CommentModal } from "@/components/CommentModal";
import { SendConfirmModal } from "@/components/SendConfirmModal";
import { Toast, type ToastMessage } from "@/components/Toast";

const WORKER_URL = import.meta.env.VITE_API_URL ?? "";
const LOGIN_URL = `${WORKER_URL}/api/auth/login`;

type State =
  | { kind: "checking" }
  | { kind: "logged_out" }
  | { kind: "loaded"; email: string; items: ApprovalItem[] }
  | { kind: "error"; message: string };

type ActionKind = "approve" | "reject" | "comment" | "send";

export default function App() {
  const [state, setState] = useState<State>({ kind: "checking" });
  const [pendingByItem, setPendingByItem] = useState<Record<string, ActionKind>>({});
  const [commentTarget, setCommentTarget] = useState<ApprovalItem | null>(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [sendTarget, setSendTarget] = useState<ApprovalItem | null>(null);
  const [sendSubmitting, setSendSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const showToast = useCallback((kind: ToastMessage["kind"], text: string) => {
    setToast({ id: Date.now(), kind, text });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me().catch(() => null);
      if (!me) {
        setState({ kind: "logged_out" });
        return;
      }
      const inbox = await api.listInbox();
      setState({ kind: "loaded", email: me.email, items: inbox.items });
    } catch (err) {
      setState({ kind: "error", message: (err as Error).message });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function setItemPending(id: string, kind: ActionKind | null) {
    setPendingByItem((prev) => {
      const next = { ...prev };
      if (kind) next[id] = kind;
      else delete next[id];
      return next;
    });
  }

  function removeItem(id: string) {
    setState((s) => (s.kind === "loaded" ? { ...s, items: s.items.filter((i) => i.id !== id) } : s));
  }

  async function handleAction(item: ApprovalItem, kind: "approve" | "reject") {
    setItemPending(item.id, kind);
    const snapshot = state.kind === "loaded" ? state.items : [];
    removeItem(item.id);
    try {
      const result = kind === "approve" ? await api.approve(item.id) : await api.reject(item.id);
      if (result.skipped) {
        showToast("info", "既に処理済みでした（冪等）");
      } else {
        showToast("success", kind === "approve" ? "承認しました ✅" : "却下しました ❌");
      }
    } catch (err) {
      setState((s) => (s.kind === "loaded" ? { ...s, items: snapshot } : s));
      showToast("error", `失敗：${(err as Error).message}`);
    } finally {
      setItemPending(item.id, null);
    }
  }

  async function handleSendSubmit() {
    if (!sendTarget) return;
    const item = sendTarget;
    setSendSubmitting(true);
    setItemPending(item.id, "send");
    const snapshot = state.kind === "loaded" ? state.items : [];
    try {
      const result = await api.approve(item.id, { sendGmail: true });
      if (result.statusUpdateFailed) {
        // 「送信済・Notion 未更新」状態（review #2 step 3 OK + step 4 NG）
        showToast(
          "error",
          `🚨 送信は成功したが Notion 更新が失敗。messageId=${result.messageId}。手動で Notion を「完了」に変更してください。`,
        );
      } else if (result.skipped) {
        showToast("info", "既に処理済みでした（冪等）");
      } else if (result.sent) {
        showToast("success", `✉️ 送信完了 + 承認 (msg=${result.messageId?.slice(0, 8)}…)`);
      } else {
        showToast("success", "承認しました ✅");
      }
      removeItem(item.id);
      setSendTarget(null);
    } catch (err) {
      const e = err as Error & { apiStatus?: number; apiBody?: any };
      setState((s) => (s.kind === "loaded" ? { ...s, items: snapshot } : s));
      const detail = e.apiBody?.error ?? e.message;
      showToast("error", `送信失敗：${detail}`);
    } finally {
      setSendSubmitting(false);
      setItemPending(item.id, null);
    }
  }

  async function handleCommentSubmit(text: string) {
    if (!commentTarget || !text) return;
    setCommentSubmitting(true);
    try {
      await api.comment(commentTarget.id, text);
      showToast("success", "コメントを Notion に追記しました 💬");
      setCommentTarget(null);
    } catch (err) {
      showToast("error", `コメント失敗：${(err as Error).message}`);
    } finally {
      setCommentSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur px-4 py-3 pt-[max(env(safe-area-inset-top),0.75rem)]">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-none">SHINGEN Sorter</h1>
            <p className="text-xs text-muted-foreground truncate">
              {state.kind === "loaded" ? state.email : "GEN承認待ち"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {state.kind === "loaded" && (
              <button
                onClick={() => void refresh()}
                className="rounded-lg bg-secondary text-secondary-foreground px-3 py-1.5 text-sm active:opacity-80"
                aria-label="再読込"
              >
                ⟳
              </button>
            )}
            {state.kind === "logged_out" && (
              <a
                href={LOGIN_URL}
                className="rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium active:opacity-80"
              >
                ログイン
              </a>
            )}
          </div>
        </div>
      </header>

      <section className="px-4 py-4 space-y-3">
        {state.kind === "checking" && (
          <p className="text-muted-foreground text-sm">読み込み中…</p>
        )}

        {state.kind === "logged_out" && (
          <div className="rounded-lg border border-border bg-card p-4 text-sm">
            <p className="font-medium">未ログイン</p>
            <p className="text-muted-foreground text-xs mt-1">
              右上の「ログイン」から Google OAuth（gen@1ne.com 限定）でログインしてください。
            </p>
          </div>
        )}

        {state.kind === "error" && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <p className="font-medium text-destructive">エラー</p>
            <p className="text-muted-foreground text-xs mt-1 break-all">{state.message}</p>
            <button
              onClick={() => void refresh()}
              className="mt-2 rounded-lg bg-secondary text-secondary-foreground px-3 py-1.5 text-xs"
            >
              再試行
            </button>
          </div>
        )}

        {state.kind === "loaded" && state.items.length === 0 && (
          <p className="text-muted-foreground text-sm">承認待ちはありません 🎉</p>
        )}

        {state.kind === "loaded" &&
          state.items.map((item) => (
            <ApprovalCard
              key={item.id}
              item={item}
              pending={pendingByItem[item.id] ?? null}
              onApprove={() => void handleAction(item, "approve")}
              onReject={() => void handleAction(item, "reject")}
              onComment={() => setCommentTarget(item)}
              onSend={() => setSendTarget(item)}
            />
          ))}
      </section>

      <CommentModal
        open={!!commentTarget}
        title={commentTarget?.title ?? ""}
        submitting={commentSubmitting}
        onClose={() => !commentSubmitting && setCommentTarget(null)}
        onSubmit={handleCommentSubmit}
      />

      <SendConfirmModal
        item={sendTarget}
        submitting={sendSubmitting}
        onClose={() => !sendSubmitting && setSendTarget(null)}
        onSubmit={handleSendSubmit}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </main>
  );
}
